import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Box, Chip, IconButton, useMediaQuery } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import DocumentsIcon from "@mui/icons-material/InsertDriveFile";
import moment from "moment";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import ComplaintConversation from "./ComplaintConversationModal";
import FilterComplaintList from "./FilterComplaintList";
import Tooltip from "Components/Common/Tooltip";
import Modal from "Components/Common/Modal";
import ImageGalleryModal from "./ImageGalleryModal";

import { getOptionLabel } from "common/enums";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { ComplaintDescription } from "./style";

import { CONSUMER_COMPLAINT } from "tagTypes";
import { useLazyConsumerComplaintQuery } from "apis/ConsumerComplaint";

const RECORDS_PER_PAGE = 10;

const chipColor = {
  1: "info",
  2: "success",
  3: "error",
};

const ViewConsumerComplaints = ({ t }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const resetPageRef = useRef();

  const lg = useMediaQuery("(min-width: 900px) and (max-width: 1535px)");
  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const [query, setQuery] = useState({});
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [documentUrls, setDocumentUrls] = useState([]);

  const [getDocumentUrlsById, { isLoading, isFetching }] =
    useLazyConsumerComplaintQuery();

  const complaintId = Number(searchParams.get("complaintId"));
  const complaintStatus = searchParams.get("status");

  const complaintIdForViewDocument = Number(
    searchParams.get("complaintIdForDocumentView")
  );

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const columns = [
    {
      title: "Consumer Number",
      field: "consumerNumber",
      columnProps: {
        width: "10%",
      },
    },
    {
      title: "Utility Name",
      field: "utility.name",
      columnProps: {
        width: "10%",
      },
    },
    {
      title: "Support Case",
      field: "supportCaseId",
      columnProps: {
        width: "15%",
      },
    },
    {
      title: "Issue",
      field: "utilityIssueName",
      columnProps: {
        width: "40%",
      },
    },

    {
      title: "Priority",
      renderCell: ({ severity }) => getOptionLabel("severityOptions", severity),
      columnProps: {
        width: "5%",
      },
    },
    {
      title: "Date",
      columnProps: {
        width: "10%",
      },
      renderCell: ({ createdAt }) =>
        moment(createdAt).isValid()
          ? convertUTCDateToLocalDate(createdAt).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Actions",
      columnProps: {
        width: "10%",
      },
      renderCell: ({ complaintStatus, id, complaintDocuments }) => {
        const status = getOptionLabel(
          "complaintStatus",
          complaintStatus
        ).toLowerCase();
        return (
          <Box width="120px">
            <IconButton
              disabled={!complaintDocuments}
              onClick={() =>
                setSearchParams({ complaintIdForDocumentView: id })
              }
            >
              <Tooltip title={t("View Documents")}>
                <DocumentsIcon
                  fontSize="small"
                  color={!complaintDocuments ? "disabled" : "primary"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            <IconButton
              onClick={() => {
                setSearchParams({
                  complaintId: id,
                  status: getOptionLabel("complaintStatus", complaintStatus),
                });
              }}
              disabled={status === "created"}
            >
              <Tooltip title={t("Discussion")}>
                <ChatIcon
                  fontSize="small"
                  color={status === "created" ? "disabled" : "primary"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
          </Box>
        );
      },
    },
    {
      title: "Status",
      renderCell: ({ complaintStatus }) => (
        <Chip
          label={getOptionLabel("complaintStatus", complaintStatus)}
          color={chipColor[complaintStatus]}
        />
      ),

      columnProps: {
        width: "5%",
      },
    },
  ];

  const renderSubRow = (data) => {
    return data.description ? (
      <ComplaintDescription>
        <p className="title">{t("Complaint Description")}: </p>
        <p className="description">{data.description}</p>
      </ComplaintDescription>
    ) : (
      <p className="text-center mb-1">{t("No Description")}</p>
    );
  };

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/user/consumer-complaint",
      tags: [CONSUMER_COMPLAINT],
      query,
    }),
    [query]
  );

  useEffect(() => {
    if (complaintIdForViewDocument) {
      (async () => {
        try {
          const { data: { complaintDocuments = [] } = {} } =
            await getDocumentUrlsById(complaintIdForViewDocument);
          setDocumentUrls(complaintDocuments);
        } catch (error) {}
      })();
    } else if (documentUrls.length) {
      setDocumentUrls([]);
    }
  }, [complaintIdForViewDocument]);

  return (
    <Box>
      {!!(
        complaintIdForViewDocument &&
        !isLoading &&
        !isFetching &&
        documentUrls?.length
      ) && (
        <Modal
          isOpen
          handleClose={() => {
            searchParams.delete("complaintIdForDocumentView");
            setSearchParams(searchParams);
          }}
          title="Documents"
          maxWidth="md"
        >
          <ImageGalleryModal images={documentUrls} />
        </Modal>
      )}
      <FilterComplaintList setQuery={setQuery} resetPage={handleResetPage} />
      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        isExpandable
        renderSubRow={renderSubRow}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 400px)"
            : md
            ? "calc(100vh - 350px)"
            : lg
            ? "calc(100vh - 320px)"
            : "calc(100vh - 270px)"
        }
      />
      {complaintId ? (
        <ComplaintConversation
          isOpen={!!complaintId}
          complaintStatus={complaintStatus}
          handleClose={() => setSearchParams({})}
          complaintId={complaintId}
        />
      ) : null}
    </Box>
  );
};

ViewConsumerComplaints.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ViewConsumerComplaints);
